import { Context } from '@nuxt/types';
import { isEmpty, omit } from 'lodash';
import { createAxios } from '../lib/axiosFactory';
import {
  designStepIsCompleted,
  designStepIsRequired,
  designStepStillNeedsToBeCompleted
} from '../lib/campaign';
import { CampaignUrlFieldName } from '../lib/constants';
import { CampaignMessageType, CampaignObjective } from '../lib/enums/campaign';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { errorParser } from '../lib/response/errorParser';
import { getSmsWeightedLength } from '../lib/utilities/sms';
import { Pages } from '../lib/enums/pages';

const defaultPageSize = 6;

const getDefaultCampaignState = () => {
  return {
    id: '',
    name: '',
    audience: {
      includedLists: [],
      excludedLists: undefined
    },
    audienceCount: 0,
    audienceCountExclusionTotal: 0,
    classicTemplateId: null,
    objective: null,
    sourceClassicTemplateId: null,
    previewHtml: '',
    manifest: {},
    sms: {
      text: '',
      html: '',
      fields: {},
      shortenUrls: false
    },
    templateData: {
      fields: ''
    },
    conditions: [],
    testSmsSentAt: undefined,
    testPhoneNumbers: [] as string[],

    contentChanges: {},
    contentSaveHandler: {},

    selectedContact: '',
    numberOfCreditsRequired: undefined,
    automation: {
      type: null,
      firstPublishedOnDate: null
    },
    previouslySentCampaignId: '',
    version: null,
    sourceTemplateId: null,
    templateId: null,
    createdAt: null,
    updatedAt: null,
    step: null,
    mmsTemplateId: null,
    scheduledSendAt: null,
    sendStartedAt: null,
    sendCompletedAt: null,
    shopify: undefined,
    internalCampaignIds: null,
    shouldNumberLookupOnAutomationTrigger: false,
    prevPages: [],
    currentPage: {},
    nextPages: [],
    hasVcard: false
  };
};

export const state = {
  campaigns: [],
  ...getDefaultCampaignState()
};

export const mutations = {
  setCampaignStep(
    state: any,
    { id, step }: { id: string; step: string }
  ): void {
    const campaignIndex = state.campaigns.findIndex(x => x.id === id);
    if (campaignIndex != -1) state.campaigns[campaignIndex].step = step;
  },

  setAutomation(
    state: any,
    { automation }: { automation: Record<string, any> }
  ): void {
    state.automation = automation;
  },

  setShortenUrls(state: any, { shortenUrls }: { shortenUrls: boolean }): void {
    state.sms.shortenUrls = shortenUrls;
  },

  setHasVcard(state: any, { hasVcard }: { hasVcard: boolean }): void {
    state.hasVcard = hasVcard;
  },
  setId(state: any, { id }: { id: string }): void {
    state.id = id;
  },

  setName(state: any, { name }: { name: string }): void {
    state.name = name;
  },

  setClassicTemplateId(
    state: any,
    { classicTemplateId }: { classicTemplateId: string }
  ): void {
    state.classicTemplateId = classicTemplateId;
  },

  setObjective(state: any, { objective }: { objective: string }): void {
    state.objective = objective;
  },

  setSourceClassicTemplateId(
    state: any,
    { sourceClassicTemplateId }: { sourceClassicTemplateId: string }
  ): void {
    state.sourceClassicTemplateId = sourceClassicTemplateId;
  },

  setAudience(state: any, { audience }: { audience: string[] }): void {
    state.audience = audience;
  },
  setAudienceCount(
    state: any,
    {
      audienceCount,
      audienceCountExclusionTotal
    }: {
      audienceCount: number;
      audienceCountExclusionTotal: number;
    }
  ): void {
    state.audienceCount = audienceCount;
    state.audienceCountExclusionTotal = audienceCountExclusionTotal;
  },

  setConditions(state: any, { conditions }: { conditions: [] }): void {
    state.conditions = conditions;
  },

  setSms(state: any, { sms }: { sms: any }): void {
    state.sms = { ...sms };
  },

  setTemplateData(state: any, { template }: { template: any }): void {
    state.templateData = template;
  },

  setTestSmsSentAt(
    state: any,
    { testSmsSentAt }: { testSmsSentAt: number }
  ): void {
    state.testSmsSentAt = testSmsSentAt;
  },

  setTestPhoneNumbers(
    state: any,

    { testPhoneNumbers }: { testPhoneNumbers: string[] }
  ): void {
    state.testPhoneNumbers = testPhoneNumbers;
  },
  setCampaigns(state: any, { campaigns }: { campaigns: [] }): void {
    state.campaigns = campaigns;
  },

  setPages(state: any, pageLinks: any): void {
    state.prevPages = pageLinks.previousPages;
    state.currentPage = pageLinks.currentPage;
    state.nextPages = pageLinks.nextPages;
  },
  setTotalCount(state: any, count: number): void {
    state.totalCount = count;
  },

  setCampaignName(
    state: any,
    { id, name }: { id: string; name: string }
  ): void {
    const campaignIndex = state.campaigns.findIndex(x => x.id === id);
    state.campaigns[campaignIndex].name = name;
  },

  removeCampaign(state: any, { id }: { id: string }): void {
    const index = state.campaigns.findIndex(campaign => campaign.id === id);
    if (index > -1) {
      state.campaigns.splice(index, 1);
    }
  },

  resetState(state: any): void {
    Object.assign(state, { ...state, ...getDefaultCampaignState() });
  },

  addCampaignToList(state: any, { campaign }): void {
    state.campaigns.splice(0, 0, campaign);
  },

  setStep(state: any, { step }): void {
    state.step = step;
  },

  setSelectedContact(state: any, { selectedContact }): void {
    state.selectedContact = selectedContact;
  },
  // This function can also be called externally via Pusher events
  // Changes in the function signature should also be made from the Pusher source
  // Pushed updates must first check the campaignId matches before updating store
  setNumberOfCreditsRequired(state: any, { credits }): void {
    state.numberOfCreditsRequired = credits;
  },
  setPreviouslySentCampaignId(state: any, { previouslySentCampaignId }): void {
    state.previouslySentCampaignId = previouslySentCampaignId;
  },
  setVersion(state: any, { version }): void {
    state.version = version;
  },
  setSourceTemplateId(state: any, { sourceTemplateId }): void {
    state.sourceTemplateId = sourceTemplateId;
  },
  setTemplateId(state: any, { templateId }): void {
    state.templateId = templateId;
  },

  setCreatedAt(state: any, { createdAt }): void {
    state.createdAt = createdAt;
  },

  setUpdatedAt(state: any, { updatedAt }): void {
    state.updatedAt = updatedAt;
  },

  setMmsTemplateId(state: any, { mmsTemplateId }): void {
    state.mmsTemplateId = mmsTemplateId;
  },

  clearCampaignState(state: any): void {
    Object.assign(state, {
      campaigns: [],
      ...getDefaultCampaignState()
    });
  },

  setRunNumberLookups(state: any, { runNumberLookups }): void {
    state.shouldNumberLookupOnAutomationTrigger = runNumberLookups;
  },

  setScheduledSendAt(state: any, { scheduledSendAt }): void {
    state.scheduledSendAt = scheduledSendAt;
  },

  setCampaignAsSending(state: any, { id, step }): void {
    state.step = step;

    const campaignIndex = state.campaigns.findIndex(x => x.id === id);
    if (campaignIndex != -1) state.campaigns[campaignIndex].step = step;
  },

  setCampaignSendingAsComplete(
    state: any,
    { id, sendCompletedAt, step }
  ): void {
    state.sendCompletedAt = sendCompletedAt;
    state.step = step;

    const campaignIndex = state.campaigns.findIndex(x => x.id === id);
    if (campaignIndex != -1) {
      state.campaigns[campaignIndex].sendCompletedAt = sendCompletedAt;
      state.campaigns[campaignIndex].step = step;
    }
  },

  setSendCompletedAt(state: any, { sendCompletedAt }): void {
    state.sendCompletedAt = sendCompletedAt;
  },

  setSendStartedAt(state: any, { sendStartedAt }): void {
    state.sendStartedAt = sendStartedAt;
  },

  setShopify(state: any, { shopify }): void {
    state.shopify = shopify ? { ...shopify } : shopify;
  },

  setInternalCampaignIds(state: any, { internalCampaignIds }): void {
    state.internalCampaignIds = internalCampaignIds;
  }
};

export const getters = {
  getWeightedSmsTextLength: (state, getters, rootState, rootGetters) => (
    text: string,
    campaignDefaultFields: any,
    shortenMyLinks: boolean
  ): number => {
    const PersonalizationFieldCharacterWeight = 10;

    return getSmsWeightedLength(
      text ?? '',
      campaignDefaultFields,
      getters.designStepIsRequired,
      rootGetters['client/campaignUrlFieldPlaceHolder']?.length,
      PersonalizationFieldCharacterWeight,
      shortenMyLinks,
      rootGetters['client/shortenedLinkDomain']
    );
  },
  shouldShortenLinks(state, getters, rootState, rootGetters) {
    return rootGetters['client/hasCustomDomain']
      ? state.sms.shortenUrls ?? true
      : false;
  },
  isShortenUrlsSelected(state: any): boolean {
    return !!state.sms.shortenUrls;
  },

  automationType(state: any): string {
    return state?.automation?.type ?? '';
  },

  isScheduledCampaign(state: any): boolean {
    return !!state.scheduledSendAt;
  },

  isSendComplete(state: any): boolean {
    return !!state.sendCompletedAt;
  },
  isSendStarted(state: any): boolean {
    return !!state.sendStartedAt;
  },

  campaign(state: any): Record<string, any> {
    return omit(state, ['campaigns']);
  },

  isCampaignLoaded: (state, getters, rootState) => (id: string): boolean => {
    return id == state.id && !isEmpty(rootState['manifest']['manifest']);
  },

  hasAudience(state: any): boolean {
    return state.audienceCount > 0;
  },
  testSmsSent(state: any): boolean {
    return !!state.testSmsSentAt;
  },
  designStepIsRequired(state): boolean {
    return designStepIsRequired(state.objective);
  },

  designStepIsCompleted(state): boolean {
    return designStepIsCompleted(state.templateId);
  },

  designStepStillNeedsToBeCompleted(state, getters): boolean {
    return designStepStillNeedsToBeCompleted(
      getters.designStepIsRequired,
      state.templateId
    );
  },

  hasSmsStep(state): boolean {
    return state.objective !== CampaignObjective.LinkOnly;
  },

  objective(state): CampaignObjective {
    return state.objective;
  },

  isSimpleSmsCampaign(state): boolean {
    return state.objective === CampaignObjective.SimpleSms;
  },

  isLinkOnlyCampaign(state): boolean {
    return state.objective === CampaignObjective.LinkOnly;
  },

  isEngagementCampaign(state): boolean {
    return state.objective === CampaignObjective.Engagement;
  },

  campaignUrlFieldLinkElement(_state, _getters, _rootState, rootGetters) {
    return `<a title='${CampaignUrlFieldName}' href='javascript:;'>${rootGetters['client/campaignUrlFieldPlaceHolder']}</a>`;
  },

  campaignUrlFieldClickableLinkElement(
    _state,
    _getters,
    _rootState,
    rootGetters
  ) {
    return `<a data-intercom-product-tour='campaign-url' title="${CampaignUrlFieldName}" href="javascript:;" onclick="window.dispatchEvent(new Event('campaignClick'))">${rootGetters['client/campaignUrlFieldPlaceHolder']}</a>`;
  }
};

export const actions = {
  async loadCampaigns(
    { commit, rootState },
    payload?
  ): Promise<ActionResponse> {
    try {
      let response;
      const includeInternal = Boolean(payload?.internal);
      // TODO: implement pagination for automation campaigns
      const size: number =
        rootState.user.pageSizes[Pages.Campaigns] || defaultPageSize;

      if ('endpoint' in payload) {
        response = await createAxios().get(payload.endpoint);
      } else {
        response = await createAxios().get('/backend/campaigns-index', {
          params: {
            clientId: rootState.user.clientId,
            ...(payload?.paged && { 'page[limit]': size }),
            ...(payload?.fields && {
              'compute[campaigns]': payload.fields
            }),
            ...(payload?.searchTerm && {
              'filter[__search:name][val]': payload.searchTerm,
              'filter[__search:name][op]': 'search'
            }),
            ...(payload?.objective && {
              'filter[objective][val]': payload.objective,
              'filter[objective][op]': 'eq'
            }),
            ...(payload?.steps && {
              steps: payload.steps
            }),
            ...(!payload?.objective && {
              'filter[objective][val]': CampaignObjective.Automation,
              'filter[objective][op]': 'neq'
            }),
            ...(payload?.sort && {
              sort: payload.sort || 'createdAt:desc'
            }),
            ...(payload?.messageType && {
              'filter[mmsTemplateId][val]':
                payload.messageType === CampaignMessageType.Mms,
              'filter[mmsTemplateId][op]': 'is_defined'
            })
          }
        });
      }
      const campaigns = response.data.data.map((c: any) => ({
        ...{ id: c.id },
        ...c.attributes
      }));
      const { links, meta } = response.data;
      commit('setCampaigns', { campaigns });
      commit(
        'user/setPreloadCampaigns',
        {
          seededCampaigns: true
        },
        { root: true }
      );
      commit('setTotalCount', meta.totalRecords);
      commit('setPages', links);

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createCampaign(
    { rootState },
    payload: Record<string, unknown>
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/campaigns-create',
        {
          data: {
            type: 'campaigns',
            attributes: payload
          }
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async campaignAudienceExportCreate(
    { commit, rootState },
    payload: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/campaign-audience-exports-create',
        {
          data: {
            type: 'campaignAudienceExports',
            attributes: payload
          }
        }
      );
      return { success: true, errors: {}, body: response };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async deleteCampaign({ commit, rootState }, { id }): Promise<ActionResponse> {
    try {
      const response = await createAxios().delete('/backend/campaigns-delete', {
        params: {
          clientId: rootState.user.clientId,
          id: id
        }
      });

      commit('removeCampaign', { id: id });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async getMmsImageBlob(
    { commit, rootState },
    { id, updatedAt, fields }
  ): Promise<any> {
    try {
      const response = await createAxios(
        process.env.TemplateRendererApiUrl
      ).post(
        '/template-renderer/mms-previews-show',
        {
          data: {
            type: 'mms',
            attributes: { fields }
          }
        },
        {
          responseType: 'blob',
          params: {
            templateId: id,
            t: updatedAt
          }
        }
      );

      return {
        success: true,
        errors: {},
        body: URL.createObjectURL(response.data)
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadCampaign(
    { commit, rootState },
    { id, fields, computed }
  ): Promise<ActionResponse> {
    commit('mmsTemplate/clearMmsTemplateStore', {}, { root: true });
    try {
      const response = await createAxios().get('/backend/campaigns-show', {
        params: {
          clientId: rootState.user.clientId,
          id: id,
          ...(fields && { 'fields[campaigns]': fields }),
          'computed[campaigns]': computed ?? 'audienceCount'
        }
      });

      if (fields) {
        return { success: true, errors: {}, body: response.data };
      }

      commit('setId', { id: response.data.data.id });
      commit('setName', { name: response.data.data.attributes.name });
      commit('setCreatedAt', {
        createdAt: response.data.data.attributes.createdAt
      });
      commit('setUpdatedAt', {
        updatedAt: response.data.data.attributes.updatedAt
      });
      if ('step' in response.data.data.attributes) {
        commit('setStep', {
          step: response.data.data.attributes.step
        });
      }

      if ('audienceCount' in response.data.data.attributes) {
        commit('setAudienceCount', {
          ...response.data.data.attributes
        });
      }
      commit('setClassicTemplateId', {
        classicTemplateId: response.data.data.attributes.classicTemplateId
      });
      commit('setObjective', {
        objective: response.data.data.attributes.objective
      });
      commit('setSourceClassicTemplateId', {
        sourceClassicTemplateId:
          response.data.data.attributes.sourceClassicTemplateId
      });
      commit('setSms', { sms: response.data.data.attributes.sms });
      commit('setTemplateData', {
        template: response.data.data.attributes.template
      });
      commit('setTestSmsSentAt', {
        testSmsSentAt: response.data.data.attributes.testSmsSentAt
      });
      commit('setTestPhoneNumbers', {
        testPhoneNumbers: response.data.data.attributes.testPhoneNumbers
      });
      commit(
        'campaign-template/setPreviewHtml',
        {
          previewHtml: response.data.data.attributes.previewHtml
        },
        { root: true }
      );
      commit('setAudience', {
        audience: response.data.data.attributes.audience
      });
      commit(
        'campaign-template/setManifest',
        response.data.data.attributes.templateManifest,
        { root: true }
      );
      if ('conditions' in response.data.data.attributes) {
        commit('setConditions', {
          conditions: response.data.data.attributes.conditions
        });
      }
      commit('setVersion', {
        version: response.data.data.attributes.version
      });
      if (response.data.data.attributes.sourceTemplateId)
        commit('setSourceTemplateId', {
          sourceTemplateId: response.data.data.attributes.sourceTemplateId
        });
      if (response.data.data.attributes.templateId)
        commit('setTemplateId', {
          templateId: response.data.data.attributes.templateId
        });
      commit('setMmsTemplateId', {
        mmsTemplateId: response.data.data.attributes.mmsTemplateId || null
      });
      if (response.data.data.attributes.scheduledSendAt) {
        commit('setScheduledSendAt', {
          scheduledSendAt: response.data.data.attributes.scheduledSendAt
        });
      }
      if (response.data.data.attributes.sendCompletedAt) {
        commit('setSendCompletedAt', {
          sendCompletedAt: response.data.data.attributes.sendCompletedAt
        });
      }
      if (response.data.data.attributes.sendStartedAt) {
        commit('setSendStartedAt', {
          sendStartedAt: response.data.data.attributes.sendStartedAt
        });
      }
      if (response.data.data.attributes.automation) {
        commit('setAutomation', {
          automation: response.data.data.attributes.automation
        });
      }
      if (response.data.data.attributes.shopify) {
        commit('setShopify', {
          shopify: response.data.data.attributes.shopify
        });
      }
      commit('setRunNumberLookups', {
        runNumberLookups:
          response.data.data.attributes.shouldNumberLookupOnAutomationTrigger ??
          false
      });
      commit('setInternalCampaignIds', {
        internalCampaignIds: response.data.data.attributes.internalCampaignIds
      });
      if (response.data.data.attributes.hasVcard) {
        commit('setHasVcard', {
          hasVcard: response.data.data.attributes.hasVcard
        });
      }
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async initialiseCampaign(
    { dispatch, getters },
    { id, fields }
  ): Promise<void> {
    if (!getters.isCampaignLoaded(id)) {
      await dispatch('loadCampaign', {
        id: id,
        fields: fields
      });
    }
  },

  async updateCampaign(
    { commit, rootState },
    { id, payload, fields, computed }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch(
        '/backend/campaigns-update',
        {
          data: {
            type: 'campaigns',
            attributes: payload
          }
        },
        {
          params: {
            id: id,
            ...(fields && { 'fields[campaigns]': fields }),
            ...(computed && { 'computed[campaigns]': computed })
          }
        }
      );

      if (response.data.data.scheduledSendAt) {
        commit('setScheduledSendAt', {
          scheduledSendAt: response.data.data.attributes.scheduledSendAt
        });
      }

      if (response.data.data.attributes.sendCompletedAt) {
        commit('setSendCompletedAt', {
          sendCompletedAt: response.data.data.attributes.sendCompletedAt
        });
      }

      if (response.data.data.attributes.sendStartedAt) {
        commit('setSendStartedAt', {
          sendStartedAt: response.data.data.attributes.sendStartedAt
        });
      }

      if ('step' in response.data.data.attributes) {
        commit('setStep', {
          step: response.data.data.attributes.step
        });
      }

      if ('objective' in response.data.data.attributes) {
        commit('setObjective', {
          objective: response.data.data.attributes.objective
        });
      }

      if ('classicTemplateId' in response.data.data.attributes) {
        commit('setClassicTemplateId', {
          classicTemplateId: response.data.data.attributes.classicTemplateId
        });
      }

      if ('sourceClassicTemplateId' in response.data.data.attributes) {
        commit('setSourceClassicTemplateId', {
          sourceClassicTemplateId:
            response.data.data.attributes.sourceClassicTemplateId
        });
      }

      if ('audience' in response.data.data.attributes) {
        commit('setAudience', {
          audience: response.data.data.attributes.audience
        });
      }

      if ('audienceCount' in response.data.data.attributes) {
        commit('setAudienceCount', {
          ...response.data.data.attributes
        });
      }

      if ('conditions' in response.data.data.attributes) {
        commit('setConditions', {
          conditions: response.data.data.attributes.conditions
        });
      }

      if (response.data.data.attributes.sms) {
        commit('setSms', { sms: response.data.data.attributes.sms });
      }

      if (response.data.data.attributes.sourceTemplateId)
        commit('setSourceTemplateId', {
          sourceTemplateId: response.data.data.attributes.sourceTemplateId
        });

      if (response.data.data.attributes.templateId)
        commit('setTemplateId', {
          templateId: response.data.data.attributes.templateId
        });
      if (response.data.data.attributes.mmsTemplateId)
        commit('setMmsTemplateId', {
          mmsTemplateId: response.data.data.attributes.mmsTemplateId
        });
      if (response.data.data.attributes.automation) {
        commit('setAutomation', {
          automation: response.data.data.attributes.automation
        });
      }
      if (response.data.data.attributes.hasVcard) {
        commit('setHasVcard', {
          hasVcard: response.data.data.attributes.hasVcard ?? false
        });
      }
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async updateCampaignPreview(
    { commit, rootState },
    {
      campaignId,
      preview,
      recipientId
    }: { campaignId: string; preview: boolean; recipientId?: string }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().patch(
        '/backend/campaign-previews-update',
        {
          data: {
            type: 'campaign-previews',
            attributes: {
              preview: preview,
              recipientId: recipientId
            }
          }
        },
        {
          params: {
            campaignId: campaignId
          }
        }
      );

      commit(
        'campaign-template/setPreviewHtml',
        {
          previewHtml: response.data.data.attributes.previewHtml
        },
        { root: true }
      );
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async duplicateCampaign(
    { commit, rootState },
    { id }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/campaign-clones-create',
        {},
        {
          params: {
            campaignId: id,
            clientId: rootState.user.clientId
          }
        }
      );
      commit('addCampaignToList', {
        campaign: {
          ...response.data.data.attributes,
          id: response.data.data.id
        }
      });
      commit('setScheduledSendAt', {
        scheduledSendAt: null
      });
      commit('setSendStartedAt', {
        sendStartedAt: null
      });
      commit('setSendCompletedAt', {
        sendCompletedAt: null
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  // Calling this function will always initially set numberOfCreditsRequired to undefined.
  // Bear this in mind if watching numberOfCreditsRequired.
  // Valid values for numberOfCreditsRequired include:
  // a positive integer, representing number of credits required
  // null, representing an error occurred
  async calculateCreditsRequired({ commit }, { id }): Promise<ActionResponse> {
    try {
      commit('setNumberOfCreditsRequired', {
        credits: undefined,
        campaignId: id
      });

      const response: any = await createAxios().patch(
        '/backend/campaign-credit-calculations-create',
        {},
        {
          params: {
            campaignId: id
          }
        }
      );
      commit('setNumberOfCreditsRequired', {
        credits: response.data.data.attributes.credits
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadClassicCampaignTemplateImagePreview(
    { commit, rootState },
    { classicTemplateId, updatedAt, isSharedTemplate }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios(
        process.env.TemplateRendererApiUrl
      ).get('/template-renderer/classic-previews-show', {
        responseType: 'blob',
        params: {
          classicTemplateId,
          isSharedTemplate,
          t: updatedAt
        }
      });
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async getCampaignTemplateImagePreview(
    { commit, rootState },
    { templateId, updatedAt, scheduledSendAt, isSharedTemplate, clientId }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios(
        process.env.TemplateRendererApiUrl
      ).get('/template-renderer/public-previews-show', {
        responseType: 'blob',
        params: {
          templateId,
          isSharedTemplate,
          t: updatedAt ?? scheduledSendAt,
          ...(clientId ? { partitionKey: clientId } : {})
        }
      });

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async sendCampaign(
    _context,
    { id, scheduledSendAt, shouldResumeAfterQuietHours }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/campaign-sends-create',
        {
          data: {
            type: 'CampaignSendCreate',
            attributes: scheduledSendAt
              ? {
                  scheduledSendAt: scheduledSendAt
                }
              : {},
            shouldResumeAfterQuietHours
          }
        },
        {
          params: {
            campaignId: id
          }
        }
      );
      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async downloadLinkOnlyCampaign(
    _context: Context,
    { id }: { id: string }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/campaign-link-only-downloads-create',
        {},
        {
          params: {
            campaignId: id
          }
        }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createContactDataSubmissionExport(
    { commit },
    payload: any
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().post(
        '/backend/contact-data-submission-exports-create',
        {
          data: {
            type: 'contactDataSubmissionExports',
            attributes: payload
          }
        }
      );

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async cancelCampaign(
    { state, commit },
    { id, action }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios().delete(
        '/backend/campaign-sends-delete',
        {
          params: {
            campaignId: id,
            action: action || 'reset'
          }
        }
      );

      commit('setScheduledSendAt', {
        scheduledSendAt: null
      });
      commit('setSendStartedAt', {
        sendStartedAt: null
      });
      commit('setSendCompletedAt', {
        sendCompletedAt: null
      });
      commit('setStep', {
        step: response.data.data.step
      });

      return { success: true, errors: {}, body: {} };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
